"use client";

import React from "react";
import { useSearchParams, useRouter, usePathname, useParams } from "next/navigation";
import queryString from "query-string";
import { isEmpty, difference, omit, omitBy } from "lodash-es";
import { useFormik } from "formik";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import useCurrency from "@/hooks/useCurrency";
import { buildUrl, parseCatchallParams, buildMapQuery } from "@ide/shared/lib/helpers";

const initialFormValues = {
  // internal
  _areasOrProjects: "",
  // what we'll be sending
  projects: [],
  developers: [],
  any_areas: [],
  availability: ["available", "prelaunch"],
  unit_types: [],
  completion_max: undefined,
  number_of_bedrooms: [],
  size_min: undefined,
  amenities: [],
  max_distance_to_metro: undefined,
  max_distance_to_golf_course: undefined,
  price_min: undefined,
  price_max: undefined,
  is_payment_plan_post_handover: undefined,
  payment_plan_triggers: [],
  min_fixed_guaranteed_roi: undefined,
  min_fixed_guaranteed_roi_duration: undefined,
};

const usePropertySearch = () => {
  // const { currentCurrency } = useCurrency();
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const searchParamsRaw = useSearchParams();

  const searchParams = React.useMemo(() => {
    return buildMapQuery({
      // https://stackoverflow.com/questions/8648892/how-to-convert-url-parameters-to-a-javascript-object?page=1&tab=scoredesc#tab-top
      searchParams: Object.fromEntries(new URLSearchParams(searchParamsRaw)),
    });
  }, [searchParamsRaw]);

  const valuesFromUrl = React.useMemo(() => {
    const paramsParsed = parseCatchallParams(params?.params);
    const query = buildMapQuery({ params: paramsParsed });

    return query;
  }, [params]);

  const buildMapUrl = React.useCallback(({ values, query }) => {
    const params = [];

    // because it's just a string that we push this will appear in the url like /map/Jumeirah-Village-Circle/foo=bar
    if (values._areasOrProjects) {
      params.push(values._areasOrProjects);
    }

    Object.keys(initialFormValues).forEach((key) => {
      if (
        key === "_areasOrProjects" ||
        values[key] === undefined ||
        values[key] === null ||
        (Array.isArray(values[key]) && values[key].length === 0)
      ) {
        return;
      }

      if (
        key === "projects" &&
        Array.isArray(values["developers"]) &&
        values["developers"].length > 0
      ) {
        params.push({ search_target: "project" });
        return;
      }

      if (key === "any_areas") {
        // params.push({ search_target: "area" });
        return;
      }
      params.push({ [key]: values[key] });
    });

    return buildUrl({ url: "/map", catchallParams: params, query });
  }, []);

  const { values, errors, handleChange, handleSubmit, setFieldValue, setValues, resetForm } =
    useFormik({
      initialValues: {
        ...initialFormValues,
        ...valuesFromUrl,
        _areasOrProjects: (() => {
          if (Array.isArray(valuesFromUrl.any_areas) && valuesFromUrl.any_areas.length > 0) {
            return valuesFromUrl.any_areas[0];
          }
          if (Array.isArray(valuesFromUrl.projects) && valuesFromUrl.projects.length > 0) {
            return valuesFromUrl.projects[0];
          }

          return initialFormValues._areasOrProjects;
        })(),
      },
      enableReinitialize: true,
      validateOnChange: true,
      validate: (values) => {
        const errors = {};

        if (
          !values.availability ||
          (Array.isArray(values.availability) && values.availability.length === 0)
        ) {
          errors.availability = "At least one selection required";
        }

        return errors;
      },

      // onReset: () => {
      //   if (pathname === "/map") {
      //     router.push("/map");
      //   }
      // },

      onSubmit: (values) => {
        router.push(
          buildMapUrl({
            values,
          }),
        );
      },
    });

  const filtersCount = React.useMemo(() => {
    const filterValues = {
      ...initialFormValues,
      ...valuesFromUrl,
    };

    const isFilterActive = (key: string) => {
      if (key.startsWith("_")) {
        return false;
      }

      if (key === "projects") {
        return false;
      }

      if (key === "developers") {
        if (Array.isArray(filterValues["projects"]) && filterValues["projects"].length > 0) {
          return false;
        }
      }

      if (!Array.isArray(filterValues[key]) && !isNaN(filterValues[key])) {
        return true;
      }

      if (Array.isArray(filterValues[key]) && filterValues[key].length > 0) {
        return true;
      }

      if (!isEmpty(filterValues[key])) {
        return true;
      }

      return false;
    };

    // we only care about initialFormValues
    const _filtersCount = Object.keys(initialFormValues).reduce((acc, curr) => {
      if (isFilterActive(curr)) {
        return acc + 1;
      }

      return acc;
    }, 0);

    // console.log({ _filtersCount });

    return _filtersCount;
  }, [valuesFromUrl]);

  return {
    filtersCount,
    values,
    valuesFromUrl,
    searchParams,
    errors,
    setFieldValue,
    resetForm: ({ values }) => setValues({ ...initialFormValues, ...values }),
    // resetForm: ({ values }) => resetForm({ values: { ...initialFormValues, ...values } }),
    handleSubmit,
    buildMapUrl,
  };
};

export default usePropertySearch;
